import React, { useState, useEffect } from "react"
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Select,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Empty,
  Tooltip,
  Divider,
  Alert,
  Grid,
  message,
} from "antd"
import {
  ToolOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  BarcodeOutlined,
  CalendarOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import { enumToArray } from "helpers/service_helper"
import { PackageCompanyType } from "models/types/package-company-type"
import {
  getPackageInstaller as getPackageInstallerApi,
  getProjectPackagesApi,
  updateProjectPackageInstallerApi,
} from "helpers/backend_helper"

const { Text } = Typography
const { useBreakpoint } = Grid

// Mobile Card Component
const MobilePackageCard = ({ record, index, onEdit, t }) => {
  const isSubmitted = record.submit

  return (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        borderRight: isSubmitted ? "4px solid #52c41a" : "4px solid #faad14",
      }}
      bodyStyle={{ padding: 12 }}
    >
      <Row gutter={[12, 8]}>
        {/* Header Row */}
        <Col span={24}>
          <Space
            style={{
              width: "100%",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Tag color="blue" style={{ margin: 0 }}>
              <HomeOutlined style={{ marginLeft: 4 }} />
              واحد {index + 1}
            </Tag>
            {isSubmitted ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                تایید شده
              </Tag>
            ) : (
              <Tag color="warning" icon={<ClockCircleOutlined />}>
                در انتظار
              </Tag>
            )}
          </Space>
        </Col>

        {/* Owner */}
        <Col span={24}>
          <Space size={4}>
            <UserOutlined style={{ color: "#8c8c8c" }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              مالک:
            </Text>
            <Text strong>{record.landlordName || "-"}</Text>
          </Space>
        </Col>

        {/* Installer */}
        <Col span={24}>
          <Space size={4} align="start">
            <ToolOutlined style={{ color: "#8c8c8c" }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              نصاب:
            </Text>
            {record.packageInstallerName ? (
              <Text strong style={{ color: "#1890ff" }}>
                {record.packageInstallerName}
                {record.packageInstallerMobileNumber && (
                   <a
                href={`tel:${record.packageInstallerMobileNumber}`}
                style={{ color: "inherit", textDecoration: "none" }}
              > / {record.packageInstallerMobileNumber}</a>
                )}
              </Text>
            ) : (
              <Text type="secondary">انتخاب نشده</Text>
            )}
          </Space>
        </Col>

        {/* Serial Number */}
        {record.serialNumber && (
          <Col span={24}>
            <Space size={4}>
              <BarcodeOutlined style={{ color: "#8c8c8c" }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                سریال:
              </Text>
              <Tag color="cyan" style={{ margin: 0 }}>
                {record.serialNumber}
              </Tag>
            </Space>
          </Col>
        )}

        {/* Date */}
        {record.solarCreated && (
          <Col span={24}>
            <Space size={4}>
              <CalendarOutlined style={{ color: "#8c8c8c" }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                تاریخ:
              </Text>
              <Text style={{ fontSize: 12 }}>{record.solarCreated}</Text>
            </Space>
          </Col>
        )}

        {/* Action Button */}
        <Col span={24} style={{ marginTop: 8 }}>
          {!isSubmitted && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              block
              style={{ borderRadius: 8 }}
            >
              انتخاب نصاب
            </Button>
          )}
        </Col>
      </Row>
    </Card>
  )
}

export const ProjectPackages = ({ rowData, mainProps, openNotification }) => {
  const { t } = mainProps

  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [packageCompanyTypes, setPackageCompanyTypes] = useState([])
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [selectedInstallerId, setSelectedInstallerId] = useState(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [currentPackage, setCurrentPackage] = useState(null)
  const [lstPackageInstaller, setLstPackageInstaller] = useState([])
  const [installerLoading, setInstallerLoading] = useState(false)
  const [projectPackages, setProjectPackages] = useState([])
  const [packagesLoading, setPackagesLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)

  // Load project packages from API
  const loadProjectPackages = async () => {
    if (!rowData?.id) return

    setPackagesLoading(true)
    try {
      const response = await getProjectPackagesApi(rowData.id)
      setProjectPackages(response || [])
    } catch (error) {
      message.error("خطا در دریافت لیست پکیج‌ها")
      setProjectPackages([])
    } finally {
      setPackagesLoading(false)
    }
  }

  useEffect(() => {
    setPackageCompanyTypes(enumToArray(PackageCompanyType))
    loadProjectPackages()
  }, [rowData?.id])

  // Load installers by company name
  const loadInstallersByCompany = async companyName => {
    setInstallerLoading(true)
    try {
      const response = await getPackageInstallerApi()
      const filtered = response.filter(x => x.companyName === companyName)
      setLstPackageInstaller(filtered)
    } catch (error) {
      message.error("خطا در دریافت لیست نصاب‌ها")
      setLstPackageInstaller([])
    } finally {
      setInstallerLoading(false)
    }
  }

  // Handle company selection to filter installers
  const handleCompanyChange = async value => {
    setSelectedCompany(value)
    setSelectedInstallerId(null)

    const selectedCompanyType = packageCompanyTypes.find(p => p.id === value)
    if (selectedCompanyType) {
      const companyName = t("enums." + selectedCompanyType.name)
      await loadInstallersByCompany(companyName)
    }
  }

  // Open edit modal
  const handleEdit = record => {
    setCurrentPackage(record)
    setSelectedCompany(record.packageCompanyTypeName)
    setSelectedInstallerId(record.packageInstallerId)
    setIsModalVisible(true)
    // Load initial installers
    loadInstallersByCompany("ایران رادیاتور")
  }

  // Handle save
  const handleSave = async () => {
    if (!selectedInstallerId) {
      openNotification("warning", "top", "لطفاً نصاب را انتخاب کنید")
      return
    }

    setSaveLoading(true)
    try {
      await updateProjectPackageInstallerApi({
        projectPackageId: currentPackage.id,
        packageInstallerId: selectedInstallerId,
        gasProjectId: rowData.id,
      })

      message.success("نصاب با موفقیت ثبت شد")
      setIsModalVisible(false)
      setCurrentPackage(null)
      setSelectedInstallerId(null)
      setSelectedCompany(null)
      // Reload packages after save
      await loadProjectPackages()
    } catch (error) {
      message.error(error || "خطا در ثبت نصاب")
    } finally {
      setSaveLoading(false)
    }
  }

  // Table columns for desktop
  const columns = [
    {
      title: "واحد",
      dataIndex: "fileNumber",
      key: "fileNumber",
      width: 80,
      render: (text, record, index) => <Tag color="blue">واحد {index + 1}</Tag>,
    },
    {
      title: "مالک",
      dataIndex: "landlordName",
      key: "landlordName",
      ellipsis: true,
      render: text => (
        <Space>
          <UserOutlined />
          <Text>{text || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "نصاب",
      dataIndex: "packageInstallerName",
      key: "packageInstallerName",
      render: (text, record) => (
        <Space direction="horizontal" size={0}>
          {text ? (
            <>
              <Text strong> {text} </Text>
              {record.submit && (
                <Tag
                  color="success"
                  icon={<CheckCircleOutlined />}
                  style={{ marginTop: 4 }}
                >
                  تایید شده
                </Tag>
              )}
              <Text> - {record.packageInstallerMobileNumber || "-"}</Text>
            </>
          ) : (
            <Tag color="warning" icon={<ClockCircleOutlined />}>
              انتخاب نشده
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "سریال",
      dataIndex: "serialNumber",
      key: "serialNumber",
      render: text =>
        text ? (
          <Tooltip title={text}>
            <Tag icon={<BarcodeOutlined />} color="cyan">
              {text.length > 15 ? text.substring(0, 15) + "..." : text}
            </Tag>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "تاریخ ثبت",
      dataIndex: "solarCreated",
      key: "solarCreated",
      width: 100,
      render: text => <Text type="secondary">{text || "-"}</Text>,
    },
    {
      title: "عملیات",
      key: "action",
      width: 130,
      fixed: "right",
      render: (_, record) =>
        record.submit ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            تایید شده
          </Tag>
        ) : (
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            انتخاب نصاب
          </Button>
        ),
    },
  ]

  // Stats summary
  const totalPackages = projectPackages?.length || 0
  const submittedCount = projectPackages?.filter(p => p.submit)?.length || 0
  const pendingCount = totalPackages - submittedCount

  return (
    <div>
      {/* Info Card */}
      <Alert
        message="راهنما"
        description={
          (<>
          <p>برای پروژه‌های با بیش از یک پکیج، لیست پکیج‌ها نمایش داده می‌شود. در این بخش می‌توانید نصاب هر پکیج را انتخاب یا ویرایش کنید.</p>
          <p>بعداز انتخاب نصاب با شماره تماس او می‌توانید با وی ارتباط برقرار کنید.</p>
          </>
          )
        }
        type="info"
        showIcon
        style={{ marginBottom: 8, borderRadius: 8 }}
      />
      {packagesLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <Spin spinning />
        </div>
      ) : (
        <>
          {/* Stats Row */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={8}>
              <Card
                size="small"
                style={{
                  textAlign: "center",
                  borderRadius: 8,
                  background: "#e6f7ff",
                }}
                bodyStyle={{ padding: isMobile ? 8 : 12 }}
              >
                <Text
                  strong
                  style={{ fontSize: isMobile ? 18 : 24, color: "#1890ff" }}
                >
                  {totalPackages}
                </Text>
                <Text
                  type="secondary"
                  style={{ display: "block", fontSize: isMobile ? 10 : 12 }}
                >
                  کل پکیج‌ها
                </Text>
              </Card>
            </Col>
            <Col xs={8}>
              <Card
                size="small"
                style={{
                  textAlign: "center",
                  borderRadius: 8,
                  background: "#f6ffed",
                }}
                bodyStyle={{ padding: isMobile ? 8 : 12 }}
              >
                <Text
                  strong
                  style={{ fontSize: isMobile ? 18 : 24, color: "#52c41a" }}
                >
                  {submittedCount}
                </Text>
                <Text
                  type="secondary"
                  style={{ display: "block", fontSize: isMobile ? 10 : 12 }}
                >
                  تایید شده
                </Text>
              </Card>
            </Col>
            <Col xs={8}>
              <Card
                size="small"
                style={{
                  textAlign: "center",
                  borderRadius: 8,
                  background: "#fffbe6",
                }}
                bodyStyle={{ padding: isMobile ? 8 : 12 }}
              >
                <Text
                  strong
                  style={{ fontSize: isMobile ? 18 : 24, color: "#faad14" }}
                >
                  {pendingCount}
                </Text>
                <Text
                  type="secondary"
                  style={{ display: "block", fontSize: isMobile ? 10 : 12 }}
                >
                  در انتظار
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Packages List */}
          <Spin spinning={packagesLoading}>
            <Card
              title={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <ToolOutlined />
                    <span>لیست پکیج‌های واحدها</span>
                  </Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadProjectPackages}
                    loading={packagesLoading}
                    size="small"
                  >
                    {!isMobile && 'بروزرسانی'}
                  </Button>
                </Space>
              }
              size="small"
              style={{ borderRadius: 8 }}
              bodyStyle={{ padding: isMobile ? 12 : 16 }}
            >
              <Spin spinning={packagesLoading}>
              {isMobile ? (
                // Mobile Card View
                <div>
                  {projectPackages?.length > 0 ? (
                    projectPackages.map((record, index) => (
                      <MobilePackageCard
                        key={record.id}
                        record={record}
                        index={index}
                        onEdit={handleEdit}
                        t={t}
                      />
                    ))
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="پکیجی ثبت نشده است"
                    />
                  )}
                </div>
              ) : (
                // Desktop Table View
                <Table
                  columns={columns}
                  dataSource={projectPackages}
                  rowKey="id"
                  loading={packagesLoading}
                  pagination={false}
                  size="small"
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="پکیجی ثبت نشده است"
                      />
                    ),
                  }}
                  scroll={{ x: 600 }}
                />
              )}
            </Spin>
            </Card>
          </Spin>

          {/* Edit Modal */}
          <Modal
            title={
              <Space>
                <ToolOutlined />
                <span>انتخاب نصاب</span>
              </Space>
            }
            open={isModalVisible}
            onCancel={() => {
              setIsModalVisible(false)
              setCurrentPackage(null)
            }}
            footer={[
              <Button
                key="cancel"
                icon={<CloseOutlined />}
                onClick={() => setIsModalVisible(false)}
              >
                انصراف
              </Button>,
              <Button
                key="submit"
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saveLoading}
                disabled={!selectedCompany || !selectedInstallerId}
              >
                ذخیره
              </Button>,
            ]}
            width={isMobile ? "95%" : 600}
            centered
            style={{ maxWidth: 600 }}
          >
            <div style={{ padding: "12px 0" }}>
              {/* Current Package Info */}
              {currentPackage && (
                <Card
                  size="small"
                  style={{
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #e8ecef 100%)",
                    borderRadius: 8,
                    marginBottom: 16,
                  }}
                >
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12}>
                      <Space size={4}>
                        <UserOutlined style={{ color: "#8c8c8c" }} />
                        <Text type="secondary">مالک:</Text>
                        <Text strong>{currentPackage.landlordName}</Text>
                      </Space>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Space size={4}>
                        <HomeOutlined style={{ color: "#8c8c8c" }} />
                        <Text type="secondary">پرونده:</Text>
                        <Text strong style={{ fontFamily: "tahoma" }}>
                          {currentPackage.fileNumber}
                        </Text>
                      </Space>
                    </Col>
                    {currentPackage.address && (
                      <Col span={24}>
                        <Space size={4} align="start">
                          <EnvironmentOutlined style={{ color: "#8c8c8c" }} />
                          <Text type="secondary">آدرس:</Text>
                          <Text style={{ fontSize: 12 }}>
                            {currentPackage.address}
                          </Text>
                        </Space>
                      </Col>
                    )}
                    {currentPackage.serialNumber && (
                      <Col span={24}>
                        <Space size={4}>
                          <BarcodeOutlined style={{ color: "#8c8c8c" }} />
                          <Text type="secondary">سریال:</Text>
                          <Tag color="cyan">{currentPackage.serialNumber}</Tag>
                        </Space>
                      </Col>
                    )}
                  </Row>
                </Card>
              )}

              <Divider style={{ margin: "12px 0" }} />

              <Row gutter={[16, 16]}>
                {/* Company Selection */}
                <Col span={24}>
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    شرکت سازنده
                  </Text>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="انتخاب شرکت"
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    allowClear
                    size={isMobile ? "large" : "middle"}
                  >
                    {packageCompanyTypes
                      .filter(p => p.id !== 0)
                      .map(company => (
                        <Select.Option key={company.id} value={company.id}>
                          {t("enums." + company.name)}
                        </Select.Option>
                      ))}
                  </Select>
                </Col>

                {/* Installer Selection */}
                <Col span={24}>
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    انتخاب نصاب
                  </Text>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="جستجو و انتخاب نصاب"
                    showSearch
                    value={selectedInstallerId}
                    onChange={value => setSelectedInstallerId(value)}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    loading={installerLoading}
                    notFoundContent={
                      installerLoading ? (
                        <Spin size="small" />
                      ) : (
                        "نصابی یافت نشد"
                      )
                    }
                    size={isMobile ? "large" : "middle"}
                    options={lstPackageInstaller?.map(installer => ({
                      value: installer.id,
                      label: `${installer.fullName || ""} / ${
                        installer.mobileNumber || ""
                      } / ${installer.companyName || ""}`.trim(),
                    }))}
                  />
                </Col>
              </Row>
            </div>
          </Modal>
        </>
      )}
    </div>
  )
}

export default ProjectPackages
